import { ReactIcons, VideoControlsOverlay } from "@kalyzee/kalyzee-ui";
import { Touchable } from "@kalyzee/kast-web-sdk/web-components";
import { PlayerActionItem, PlayerWorkBox, PlayerWorkBoxProps } from "./PlayerWorkBox";
import { MeetingSession, MeetingSessionHighlightingState, MeetingSessionMedia, MeetingState } from "@kalyzee/kast-web-sdk/websocket";
import { isSuperUser, MeetingContainerAPI, useMeetingSelector } from "@kalyzee/kast-web-sdk";
import React, { useCallback } from "react";
import { getNormalUser } from "../../helpers/user";
import { LayoutType } from "../../interfaces/ui";

import styles from "./MasterPlayerWorkBox.module.css";

/**
 * PTZ camera controls — directional pad + zoom buttons rendered as a full
 * overlay over the player. Each callback receives `true` on press-in and
 * `false` on press-out so the consumer can implement hold-to-move.
 */
export interface MasterPlayerControlsOverlay {
  type: "controls";
  onRight?: (on: boolean) => void;
  onLeft?: (on: boolean) => void;
  onUp?: (on: boolean) => void;
  onDown?: (on: boolean) => void;
  onZoomIn?: (on: boolean) => void;
  onZoomOut?: (on: boolean) => void;
}

/**
 * Click-anywhere selection overlay — full-area dark overlay with a centered
 * directional icon. Use this when you want the entire player area to act as
 * a focus target, instead of (or in addition to) a small action button.
 */
export interface MasterPlayerSelectionOverlay {
  type: "selection";
  onSelect: () => void;
  /** Drives the directional icon (down/up/left/right). */
  layout: LayoutType;
}

export type MasterPlayerOverlayContent =
  | React.ReactNode
  | MasterPlayerControlsOverlay
  | MasterPlayerSelectionOverlay;

const isTypedOverlay = (value: unknown): value is MasterPlayerControlsOverlay | MasterPlayerSelectionOverlay =>
  !!value && typeof value === "object" && "type" in (value as object);

const focusIconForLayout = (layout: LayoutType) => {
  switch (layout) {
    case LayoutType.Top: return ReactIcons.SelectMainDown;
    case LayoutType.Bottom: return ReactIcons.SelectMainVertical;
    case LayoutType.Left: return ReactIcons.SelectMainRight;
    case LayoutType.Right: return ReactIcons.SelectMainHorizontal;
    default: return ReactIcons.Expand;
  }
};

export interface MasterPlayerWorkBoxProps {
  meetingAPI: MeetingContainerAPI;
  children: React.ReactNode;
  session: MeetingSession;
  mediaSession: MeetingSessionMedia;
  overlayMode?: PlayerWorkBoxProps["overlayMode"];
  isSmall?: boolean;
  isPlayingSound?: boolean;
  /**
   * Either a typed overlay variant (rendered into a built-in component) or a
   * free-form ReactNode for fully custom overlays.
   */
  overlayContent?: MasterPlayerOverlayContent;
  topbarActions?: PlayerActionItem[];
  overlayActions?: PlayerActionItem[];
  mediaButtons?: boolean;
  localStream?: boolean;
  extraHeaderContent?: React.ReactNode;
  className?: string;
  topBarClassName?: string;
  boxProps?: React.HTMLAttributes<HTMLDivElement>;
}

export const MasterPlayerWorkBox = React.memo(({
  meetingAPI,
  children,
  session,
  mediaSession,
  overlayMode,
  overlayContent,
  topbarActions,
  overlayActions,
  isSmall,
  isPlayingSound,
  mediaButtons,
  localStream,
  extraHeaderContent,
  className,
  topBarClassName,
  boxProps,
}: MasterPlayerWorkBoxProps) => {
  const [meetingOngoing] = useMeetingSelector(meetingAPI, "meeting", meeting => meeting?.state === MeetingState.RUNNING);
  const [sessionMe] = useMeetingSelector(meetingAPI, "session");
  const isMe = sessionMe?.id === session.id;
  const { displayName, avatar } = session.user ? getNormalUser(session.user) : { displayName: undefined, avatar: undefined };

  const getTopBarLabel = () => {
    if (isMe) return "Vous";
    return displayName ?? "Utilisateur";
  };

  const getTobBarLabelInfo = () => {
    if (isSmall) return undefined;
    let label = mediaSession.extra?.label ?? "webcam";
    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  const enableControls = mediaButtons && (isMe || isSuperUser(sessionMe));

  const videoEnabled = mediaSession.video?.enabled ?? false;
  const audioEnabled = mediaSession.audio?.enabled ?? false;

  const onVideo = useCallback(() => {
    const { updater } = meetingAPI;
    updater.medias.update(session.id, mediaSession.id, {
      video: {
        enabled: !videoEnabled,
      },
    });
  }, [meetingAPI, session.id, mediaSession.id, videoEnabled]);

  const onAudio = useCallback(() => {
    const { updater } = meetingAPI;
    updater.medias.update(session.id, mediaSession.id, {
      audio: {
        enabled: !audioEnabled,
      },
    });
  }, [meetingAPI, session.id, mediaSession.id, audioEnabled]);

  const resolvedOverlayContent: React.ReactNode = (() => {
    if (!overlayContent) return undefined;
    if (!isTypedOverlay(overlayContent)) return overlayContent;
    if (overlayContent.type === "controls") {
      return (
        <VideoControlsOverlay
          onUp={overlayContent.onUp}
          onDown={overlayContent.onDown}
          onLeft={overlayContent.onLeft}
          onRight={overlayContent.onRight}
          onZoomIn={overlayContent.onZoomIn}
          onZoomOut={overlayContent.onZoomOut}
        >
          <></>
        </VideoControlsOverlay>
      );
    }
    if (overlayContent.type === "selection") {
      const Icon = focusIconForLayout(overlayContent.layout);
      return (
        <Touchable onClick={overlayContent.onSelect} className={styles.selection}>
          <Icon style={{ color: "white" }} />
        </Touchable>
      );
    }
    return undefined;
  })();

  return (
    <PlayerWorkBox
      label={getTopBarLabel()}
      userDisplayName={displayName}
      avatar={avatar}
      labelInfo={getTobBarLabelInfo()}
      overlayMode={overlayMode}
      overlayContent={resolvedOverlayContent}
      topbarActions={topbarActions}
      overlayActions={overlayActions}
      extraHeaderContent={extraHeaderContent}
      isScreenSource={mediaSession.extra?.type === "screen"}
      className={className}
      topBarClassName={topBarClassName}
      video={videoEnabled}
      audio={audioEnabled}
      onChangeVideo={enableControls ? onVideo : undefined}
      onChangeAudio={enableControls ? onAudio : undefined}
      isMe={isMe}
      isSmall={isSmall}
      isHighlighted={session.highlighting === MeetingSessionHighlightingState.ONGOING}
      isPlayingSound={isPlayingSound}
      boxProps={boxProps}
      idleAnimation={!meetingOngoing}
      mediaButtons={mediaButtons}
      localStream={localStream}
    >
      {children}
    </PlayerWorkBox>
  );
});
